import { useEffect, useRef } from "react";

// The clipping strip under the video: an iOS-photos-style trim track. Each pending clip is
// a draggable range — grab an edge to resize, drag the body to move; the video scrubs to
// the edge being dragged so the reviewer sees the exact frame. A plain click ANYWHERE on
// the track (inside a range included) seeks the video; selection happens by manipulating a
// range or via the rows below. Ranges can never overlap each other or the recording's
// already-created clips (rendered as fixed gray blocks) — drags clamp at the neighbors.
// A playhead follows currentTime (same rAF pattern as HandTimeline).

export interface ClipRange {
  key: number;
  startMs: number;
  endMs: number;
  taskId: string;
  /** Set when the range was added from an AI suggestion (sent back on confirm). */
  suggestionId?: number;
}

/** An immovable occupied window (already-created clip of this recording). */
export interface BlockedRange {
  startMs: number;
  endMs: number;
}

/** A detected idle stretch: still camera (imu) or the model's "no task / walking" (llm). */
export interface IdleLaneItem extends BlockedRange {
  source?: "imu" | "llm";
  reason?: string;
}

/** One AI-proposed window drawn on the suggestions lane above the trim track. */
export interface SuggestionLaneItem {
  id: number;
  startMs: number;
  endMs: number;
  label: string;
  color: string; // task category colour (var(--sc))
  confidence: number; // 0..1 -> opacity
  tooShort: boolean;
  added: boolean; // already in the composer
}

interface Props {
  durationMs: number;
  ranges: ClipRange[];
  blocked: BlockedRange[];
  selectedKey: number | null;
  /** Below this length a range renders as invalid (red). */
  minMs: number;
  videoRef: React.RefObject<HTMLVideoElement>;
  onSelect: (key: number) => void;
  onChange: (key: number, startMs: number, endMs: number) => void;
  /** AI suggestions lane (omitted = no lane). */
  suggestions?: SuggestionLaneItem[];
  /** Detected idle gaps (still camera) drawn under the suggestions; over 10 s breaks a clip. */
  idleGaps?: IdleLaneItem[];
  selectedSuggestionId?: number | null;
  onPickSuggestion?: (id: number) => void;
}

// while dragging, a range can't collapse below this (the 5-min rule is enforced at
// confirm time — a hard drag floor of 5 min would make coarse adjustments miserable)
export const DRAG_MIN_MS = 1000;

// pointer travel below this is a click (seek), at or beyond it a body drag (move range)
const MOVE_THRESHOLD_PX = 4;

type DragMode = "start" | "end" | "move";

function fmt(ms: number): string {
  const s = Math.round(ms / 1000);
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;
}

export default function ClipTimeline({
  durationMs,
  ranges,
  blocked,
  selectedKey,
  minMs,
  videoRef,
  onSelect,
  onChange,
  suggestions,
  idleGaps,
  selectedSuggestionId,
  onPickSuggestion,
}: Props) {
  const trackRef = useRef<HTMLDivElement>(null);
  const playheadRef = useRef<HTMLDivElement>(null);
  const dragRef = useRef<{
    key: number;
    mode: DragMode;
    grabOffsetMs: number;
    startClientX: number;
    moved: boolean;
  } | null>(null);
  // a completed drag still emits a click on pointerup; swallow that one so a resize/move
  // never doubles as a seek
  const suppressClickRef = useRef(false);

  // Playhead follows the video time.
  useEffect(() => {
    const ph = playheadRef.current;
    if (!ph || durationMs <= 0) return;
    let raf = 0;
    const tick = () => {
      const v = videoRef.current;
      if (v) {
        const pct = Math.max(0, Math.min(1, (v.currentTime * 1000) / durationMs));
        ph.style.left = `${pct * 100}%`;
      }
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [videoRef, durationMs]);

  const msAt = (clientX: number): number => {
    const track = trackRef.current;
    if (!track) return 0;
    const rect = track.getBoundingClientRect();
    const pct = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width));
    return Math.round(pct * durationMs);
  };

  const scrub = (ms: number) => {
    const v = videoRef.current;
    if (v) v.currentTime = ms / 1000;
  };

  /** The dragged range's free corridor: [end of nearest obstacle on the left, start of the
   * nearest obstacle on the right]. Obstacles = the other pending ranges + existing clips;
   * the no-overlap invariant keeps them disjoint from the dragged range, so classifying by
   * its current position is stable throughout the drag. */
  const corridorFor = (key: number): { lo: number; hi: number } => {
    const r = ranges.find((x) => x.key === key);
    if (!r) return { lo: 0, hi: durationMs };
    let lo = 0;
    let hi = durationMs;
    const obstacles = [
      ...ranges.filter((x) => x.key !== key),
      ...blocked,
    ];
    for (const o of obstacles) {
      if (o.endMs <= r.startMs) lo = Math.max(lo, o.endMs);
      if (o.startMs >= r.endMs) hi = Math.min(hi, o.startMs);
    }
    return { lo, hi };
  };

  const beginDrag = (e: React.PointerEvent, key: number, mode: DragMode) => {
    e.stopPropagation();
    const r = ranges.find((x) => x.key === key);
    if (!r) return;
    // resizing is deliberate manipulation — select right away; a body press only becomes
    // a move (and a selection) once the pointer actually travels, so a plain click seeks
    if (mode !== "move") onSelect(key);
    dragRef.current = {
      key,
      mode,
      grabOffsetMs: msAt(e.clientX) - r.startMs,
      startClientX: e.clientX,
      moved: mode !== "move",
    };
    (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
  };

  const moveDrag = (e: React.PointerEvent) => {
    const drag = dragRef.current;
    if (!drag) return;
    if (!drag.moved) {
      if (Math.abs(e.clientX - drag.startClientX) < MOVE_THRESHOLD_PX) return;
      drag.moved = true;
      onSelect(drag.key);
    }
    const r = ranges.find((x) => x.key === drag.key);
    if (!r) return;
    const ms = msAt(e.clientX);
    const { lo, hi } = corridorFor(drag.key);
    if (drag.mode === "start") {
      const start = Math.max(lo, Math.min(ms, r.endMs - DRAG_MIN_MS));
      onChange(r.key, start, r.endMs);
      scrub(start);
    } else if (drag.mode === "end") {
      const end = Math.min(hi, Math.max(ms, r.startMs + DRAG_MIN_MS));
      onChange(r.key, r.startMs, end);
      scrub(end);
    } else {
      const width = r.endMs - r.startMs;
      const start = Math.max(lo, Math.min(ms - drag.grabOffsetMs, hi - width));
      onChange(r.key, start, start + width);
      scrub(start);
    }
  };

  const endDrag = () => {
    const drag = dragRef.current;
    if (drag?.moved) suppressClickRef.current = true;
    dragRef.current = null;
  };

  if (durationMs <= 0) return null;

  const pct = (ms: number) => `${(Math.max(0, Math.min(durationMs, ms)) / durationMs) * 100}%`;
  const showLanes = (suggestions && suggestions.length > 0) || (idleGaps && idleGaps.length > 0);

  return (
    <div className="clip-lanes">
      {showLanes && (
        <div className="clip-lane-row">
          <span className="clip-lane-label">AI suggestions</span>
          <div className="clip-lane" role="list" aria-label="AI clip suggestions">
            {(suggestions ?? []).map((s) => {
              const cls = [
                "clip-suggest",
                s.tooShort ? "short" : "",
                s.added ? "added" : "",
                s.id === selectedSuggestionId ? "selected" : "",
              ]
                .filter(Boolean)
                .join(" ");
              return (
                <div
                  key={s.id}
                  role="listitem"
                  className={cls}
                  style={{
                    left: pct(s.startMs),
                    width: `${((s.endMs - s.startMs) / durationMs) * 100}%`,
                    ["--sc" as string]: s.color,
                    ["--conf" as string]: String(
                      0.35 + 0.65 * Math.max(0, Math.min(1, s.confidence)),
                    ),
                  }}
                  title={`${s.label}: ${fmt(s.startMs)}–${fmt(s.endMs)} — click to jump there`}
                  onClick={() => {
                    scrub(s.startMs);
                    onPickSuggestion?.(s.id);
                  }}
                >
                  <span className="clip-suggest-label">{s.label}</span>
                </div>
              );
            })}
          </div>
        </div>
      )}
      {showLanes && (
        <div className="clip-lane-row">
          <span className="clip-lane-label">Idle gaps</span>
          <div
            className="clip-lane idle"
            title="Still camera for over 10 s (frames + motion sensors)"
          >
            {(idleGaps ?? []).map((g, i) => (
              <div
                key={i}
                className={[
                  "clip-idle",
                  g.endMs - g.startMs < 60_000 ? "tiny" : "",
                  g.source === "llm" ? "llm" : "",
                ]
                  .filter(Boolean)
                  .join(" ")}
                style={{
                  left: pct(g.startMs),
                  width: `${((g.endMs - g.startMs) / durationMs) * 100}%`,
                }}
                title={`${
                  g.source === "llm"
                    ? `No task (${(g.reason ?? "idle").replace(/_/g, " ")})`
                    : "Camera still"
                } ${fmt(g.startMs)}–${fmt(g.endMs)} (${fmt(g.endMs - g.startMs)})`}
                onClick={() => scrub(g.startMs)}
              />
            ))}
          </div>
        </div>
      )}
      <div className={showLanes ? "clip-lane-row" : undefined}>
        {showLanes && <span className="clip-lane-label">Clips</span>}
    <div
      ref={trackRef}
      className="clip-track"
      onClick={(e) => {
        if (suppressClickRef.current) {
          suppressClickRef.current = false;
          return;
        }
        scrub(msAt(e.clientX));
      }}
      role="group"
      aria-label="Clip ranges — drag the handles to adjust; click to seek"
    >
      {blocked.map((b, idx) => (
        <div
          key={`blocked-${idx}`}
          className="clip-range blocked"
          style={{
            left: `${(b.startMs / durationMs) * 100}%`,
            width: `${((b.endMs - b.startMs) / durationMs) * 100}%`,
          }}
          title={`Existing clip: ${fmt(b.startMs)}–${fmt(b.endMs)}`}
        />
      ))}
      {ranges.map((r, idx) => {
        const left = (r.startMs / durationMs) * 100;
        const width = ((r.endMs - r.startMs) / durationMs) * 100;
        const invalid = r.endMs - r.startMs < minMs;
        const cls = [
          "clip-range",
          r.key === selectedKey ? "selected" : "",
          invalid ? "invalid" : "",
        ]
          .filter(Boolean)
          .join(" ");
        return (
          <div
            key={r.key}
            className={cls}
            style={{ left: `${left}%`, width: `${width}%` }}
            onPointerDown={(e) => beginDrag(e, r.key, "move")}
            onPointerMove={moveDrag}
            onPointerUp={endDrag}
            title={`Clip ${idx + 1}: ${fmt(r.startMs)}–${fmt(r.endMs)} — click to seek, drag to move`}
          >
            <span
              className="clip-handle left"
              onPointerDown={(e) => beginDrag(e, r.key, "start")}
              onPointerMove={moveDrag}
              onPointerUp={endDrag}
            />
            <span className="clip-range-label">{idx + 1}</span>
            <span
              className="clip-handle right"
              onPointerDown={(e) => beginDrag(e, r.key, "end")}
              onPointerMove={moveDrag}
              onPointerUp={endDrag}
            />
          </div>
        );
      })}
      <div ref={playheadRef} className="clip-playhead" />
    </div>
      </div>
    </div>
  );
}
